/**
 * Quote PDF Upload & Review
 *
 * Drop supplier quotation PDFs → Claude extracts quote header + priced lines
 * (any subset of DDP 40'HQ / 40'GP / 20'DC, LCL, FOB) → each line is matched
 * to a SKU-master item (suggestion pre-selected only when decisive) → nothing
 * is saved until Confirm, which appends price-history rows per (line, method).
 */

import React, { useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import NewProductDialog from "shared/components/NewProductDialog";
import SkuMasterService, {
  ExtractedQuote,
  ExtractedQuoteLine,
  FreightMethod,
  QuoteConfirmRow,
  SkuItem,
} from "services/SkuMasterService";

const METHOD_LABELS: Record<FreightMethod, string> = {
  "40hc": "DDP 40'HQ",
  "40gp": "DDP 40'GP",
  "20dc": "DDP 20'DC",
  lcl: "LCL",
  fob: "FOB",
};

export type NewProductInput = {
  name: string;
  sku: string;
  unitPrice: number;
  priceType: string;
  lengthIn?: number | null;
  widthIn?: number | null;
  heightIn?: number | null;
  weightOz?: number | null;
  countryOfOrigin?: string | null;
};

interface QuoteUploadDialogProps {
  open: boolean;
  onClose: () => void;
  service: SkuMasterService;
  items: SkuItem[];
  /** Every SKU in the catalog — powers the new-product SKU builder */
  existingSkus: string[];
  /** Creates the product in ShipBob; resolves the new SKU-master item, or null on failure */
  onCreateProduct: (product: NewProductInput) => Promise<SkuItem | null>;
  onSaved: (savedCount: number) => void;
}

export default function QuoteUploadDialog({
  open,
  onClose,
  service,
  items,
  existingSkus,
  onCreateProduct,
  onSaved,
}: QuoteUploadDialogProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<ExtractedQuote[]>([]);
  // Selected items per (quote index, line index) — MULTIPLE allowed, since one
  // quote line often covers several items (e.g. colors of the same sheet set).
  // Empty = skip this line.
  const [selectedItems, setSelectedItems] = useState<Record<string, SkuItem[]>>({});
  const [isDragOver, setIsDragOver] = useState(false);
  // Line the "Create new product" dialog was opened from (null = closed).
  // Items created mid-session live in extraItems until the page refetches.
  const [createLineKey, setCreateLineKey] = useState<string | null>(null);
  const [extraItems, setExtraItems] = useState<SkuItem[]>([]);

  const lineKey = (quoteIndex: number, lineIndex: number) => `${quoteIndex}:${lineIndex}`;
  const selectableItems = [...items, ...extraItems.filter(extra => !items.some(item => item.shipbobInventoryId === extra.shipbobInventoryId))];

  const lineForKey = (key: string | null): ExtractedQuoteLine | null => {
    if (!key) return null;
    const [quoteIndex, lineIndex] = key.split(":").map(Number);
    return quotes[quoteIndex]?.lines[lineIndex] ?? null;
  };

  // Prefill the new-product form from the quote line: DDP cost preferred
  // (largest container first, matching how sku_costs are quoted), FOB only
  // when the line has no DDP price at all.
  const createLine = lineForKey(createLineKey);
  const createInitial = (() => {
    if (!createLine) return undefined;
    const priceByMethod = new Map(createLine.prices.map(price => [price.method, price.unitPrice]));
    const method = (["40hc", "40gp", "20dc", "lcl", "fob"] as FreightMethod[]).find(m => priceByMethod.has(m));
    const includesSize = createLine.size
      && createLine.description.toLowerCase().includes(createLine.size.toLowerCase());
    return {
      name: includesSize || !createLine.size ? createLine.description : `${createLine.description} - ${createLine.size}`,
      unitPrice: method ? priceByMethod.get(method) : null,
      priceType: method === "fob" ? "FOB" : "DDP",
    };
  })();

  const handleCreateForLine = async (product: NewProductInput): Promise<boolean> => {
    const created = await onCreateProduct(product);
    if (!created) return false;
    setExtraItems(prev => [...prev, created]);
    if (createLineKey != null) {
      setSelectedItems(prev => ({
        ...prev,
        [createLineKey]: [...(prev[createLineKey] || []), created],
      }));
    }
    return true;
  };

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).filter(file => file.type === "application/pdf");
    if (files.length === 0) {
      setErrorMessage("Only PDF files are supported");
      return;
    }
    setIsExtracting(true);
    setErrorMessage(files.length < fileList.length ? "Skipped non-PDF files" : null);
    try {
      const result = await service.extractQuotes(files);
      setQuotes(prev => [...prev, ...result.quotes]);
      // Pre-select decisive suggestions
      setSelectedItems(prev => {
        const next = { ...prev };
        const baseIndex = quotes.length;
        result.quotes.forEach((quote, quoteOffset) => {
          quote.lines.forEach((line, lineIndex) => {
            if (line.decisive && line.suggestions[0]) {
              const item = items.find(i => i.shipbobInventoryId === line.suggestions[0].itemId);
              if (item) next[lineKey(baseIndex + quoteOffset, lineIndex)] = [item];
            }
          });
        });
        return next;
      });
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const buildRows = (): QuoteConfirmRow[] => {
    const rows: QuoteConfirmRow[] = [];
    quotes.forEach((quote, quoteIndex) => {
      quote.lines.forEach((line, lineIndex) => {
        const lineItems = selectedItems[lineKey(quoteIndex, lineIndex)] || [];
        const loadingByMethod = new Map(line.loadingQuantities.map(entry => [entry.method, entry.qty]));
        for (const item of lineItems) for (const price of line.prices) {
          rows.push({
            itemId: item.shipbobInventoryId,
            method: price.method,
            unitPrice: price.unitPrice,
            currency: quote.currency,
            manufacturer: quote.manufacturer,
            quotationNumber: quote.quotationNumber,
            lineDescription: line.description,
            quoteDate: quote.quoteDate,
            validUntil: quote.validUntil,
            moq: quote.moq,
            paymentTerms: quote.paymentTerms,
            deliveryTerms: quote.deliveryTerms,
            pcsPerCarton: line.pcsPerCarton,
            loadingQty: loadingByMethod.get(price.method) ?? null,
            sourcePdfPath: quote.sourcePdfPath,
          });
        }
      });
    });
    return rows;
  };

  const pendingRows = buildRows();

  const handleConfirm = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      const result = await service.confirmQuotes(pendingRows);
      if (!result?.ok) {
        setErrorMessage("Failed to save quotes");
        return;
      }
      onSaved(result.saved);
      setQuotes([]);
      setSelectedItems({});
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const renderLine = (quoteIndex: number, line: ExtractedQuoteLine, lineIndex: number) => {
    const key = lineKey(quoteIndex, lineIndex);
    return (
      <TableRow key={key}>
        <TableCell>
          {line.description}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {[line.dimensions, line.manufacturerSku && `mfr ${line.manufacturerSku}`, line.pcsPerCarton && `${line.pcsPerCarton}/carton`]
              .filter(Boolean).join(" • ")}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {line.prices.map(price => (
              <Chip
                key={price.method}
                size="small"
                label={`${METHOD_LABELS[price.method]}: $${price.unitPrice.toFixed(2)}`}
              />
            ))}
          </Box>
        </TableCell>
        <TableCell>
          <Autocomplete
            multiple
            size="small"
            options={selectableItems}
            value={selectedItems[key] || []}
            onChange={(_, value) => setSelectedItems(prev => ({ ...prev, [key]: value }))}
            getOptionLabel={(item) => `${item.shipbobSku || item.shipbobInventoryId} — ${item.name || ""}`}
            isOptionEqualToValue={(a, b) => a.shipbobInventoryId === b.shipbobInventoryId}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={(selectedItems[key] || []).length > 0
                  ? "Add another item…"
                  : "Pick item(s) — e.g. every color this line covers (blank = skip)"}
              />
            )}
            sx={{ minWidth: 300 }}
          />
          <Box sx={{ mt: 0.5, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {line.suggestions
              .filter(suggestion => !(selectedItems[key] || []).some(item => item.shipbobInventoryId === suggestion.itemId))
              .map(suggestion => (
                <Chip
                  key={suggestion.itemId}
                  size="small"
                  variant="outlined"
                  label={`+ ${suggestion.label} (${Math.round(suggestion.score * 100)}%)`}
                  onClick={() => {
                    const item = items.find(i => i.shipbobInventoryId === suggestion.itemId);
                    if (item) {
                      setSelectedItems(prev => ({ ...prev, [key]: [...(prev[key] || []), item] }));
                    }
                  }}
                />
              ))}
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              label="＋ Create new product"
              onClick={() => setCreateLineKey(key)}
            />
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Upload supplier quotes</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>{errorMessage}</Alert>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          multiple
          hidden
          onChange={(e) => handleFiles(e.target.files)}
        />
        <Box
          onClick={() => !isExtracting && fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (!isExtracting) handleFiles(e.dataTransfer.files);
          }}
          sx={{
            border: "2px dashed",
            borderColor: isDragOver ? "primary.main" : "#CBD5E1",
            backgroundColor: isDragOver ? "#EEF2FF" : "#F8FAFC",
            borderRadius: 2,
            p: 3,
            mb: 2,
            textAlign: "center",
            cursor: isExtracting ? "wait" : "pointer",
            transition: "all 0.15s ease",
          }}
        >
          {isExtracting ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Extracting quote data…</Typography>
            </Box>
          ) : (
            <>
              <UploadFileIcon sx={{ color: "#64748B" }} />
              <Typography variant="body2">
                Drag &amp; drop quote PDFs here, or click to choose files
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Prices are extracted per freight method (DDP 40'HQ / 40'GP / 20'DC, LCL, FOB)
                and matched to items for your review — nothing is saved until you confirm.
              </Typography>
            </>
          )}
        </Box>

        {quotes.map((quote, quoteIndex) => (
          <Box key={`${quote.sourcePdfPath}-${quoteIndex}`} sx={{ mb: 3 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2">
              {quote.fileName}
              {quote.quotationNumber && ` — ${quote.quotationNumber}`}
              {quote.manufacturer && ` (${quote.manufacturer})`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {[
                quote.quoteDate && `dated ${quote.quoteDate}`,
                quote.validUntil && `valid until ${quote.validUntil}`,
                quote.moq && `MOQ ${quote.moq}`,
                quote.deliveryTerms,
              ].filter(Boolean).join(" • ")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quote line</TableCell>
                  <TableCell width={300}>Extracted prices</TableCell>
                  <TableCell width={340}>Map to item</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quote.lines.map((line, lineIndex) => renderLine(quoteIndex, line, lineIndex))}
              </TableBody>
            </Table>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={pendingRows.length === 0 || isSaving || isExtracting}
          onClick={handleConfirm}
        >
          {isSaving ? "Saving…" : `Confirm ${pendingRows.length} price row${pendingRows.length === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>

    <NewProductDialog
      open={createLineKey != null}
      onClose={() => setCreateLineKey(null)}
      existingSkus={existingSkus}
      initial={createInitial}
      onCreate={handleCreateForLine}
    />
    </>
  );
}
